import { coreAPI } from './client';
import {
  User,
  Merchant,
  Product,
  SearchParams,
  SearchResult,
  UserAddress,
  CreateAddressRequest,
  UpdateAddressRequest,
  OrdersResponse,
  OrderTimelineEvent,
} from '@/types';
import type { RichSearchSuggestions } from '@/types/catalog';

/**
 * Result of POST /merchants (MREG-1.2). Either a created merchant (slot available)
 * or a waitlist entry (status='waitlisted' + 1-based queue `position`).
 */
/**
 * Discriminated registration outcomes (MREG-1.2 / MREG-2.1):
 * - `active`/`created` — slot available → provisioning starts.
 * - `waitlisted`        — no free slot → WaitlistScreen (+1-based `position`).
 * - `pending_approval`  — autoapprove off → admin moderation (no slot/provision).
 *
 * `pending` was removed in MREG-3.4: it only ever came from the vestigial
 * `hasRole(owner,'customer')` gate that MREG-3.1 deleted, and the status no
 * longer exists in the PG enum, the backend TS, or here.
 */
export type MerchantCreateStatus =
  | 'active'
  | 'created'
  | 'waitlisted'
  | 'pending_approval';

export interface MerchantCreateResult {
  merchantGuid: string;
  businessName: string;
  subdomain: string;
  status: MerchantCreateStatus;
  position?: number;
}

export interface MerchantStats {
  total_merchants: number;
  total_orders: number;
  total_revenue: number;
  average_rating: number;
}

export interface PopularProduct {
  product_guid: string;
  merchant_guid: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  image_url: string;
  merchant_name: string;
  merchant_subdomain: string;
  category: string;
  brand: string;
  in_stock: boolean;
  stock_status?: 'in_stock' | 'low_stock' | 'out_of_stock';
}

export interface MerchantPublicInfo {
  merchant_guid: string;
  business_name: string;
  subdomain: string;
}

export const coreAPI_Service = {
  // Platform statistics
  async getStats(): Promise<MerchantStats> {
    try {
      return await coreAPI.get<MerchantStats>('/stats', false); // Public endpoint
    } catch {
      // Return mock data if API is not available
      return {
        total_merchants: 1523,
        total_orders: 45678,
        total_revenue: 12589000,
        average_rating: 4.7
      };
    }
  },

  // Search products
  async searchProducts(params: SearchParams): Promise<SearchResult<Product>> {
    const searchParams = new URLSearchParams();

    if (params.query) searchParams.append('q', params.query);
    if (params.category) searchParams.append('category', params.category);
    if (params.filters?.min_price) searchParams.append('price_min', params.filters.min_price.toString());
    if (params.filters?.max_price) searchParams.append('price_max', params.filters.max_price.toString());
    if (params.filters?.location) searchParams.append('location', params.filters.location);
    if (params.filters?.rating) searchParams.append('rating_min', params.filters.rating.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.offset) searchParams.append('offset', params.offset.toString());

    return coreAPI.get<SearchResult<Product>>(`/search/products?${searchParams}`, false);
  },

  // Get rich search suggestions from Catalog API (SEARCH-CF-2)
  async getSearchSuggestions(query: string, limit = 5): Promise<RichSearchSuggestions> {
    return coreAPI.get<RichSearchSuggestions>(
      `/catalog/search/suggest?q=${encodeURIComponent(query)}&limit=${limit}`,
      false
    );
  },

  // Get popular products from Catalog
  async getPopularProducts(limit = 10): Promise<PopularProduct[]> {
    try {
      return await coreAPI.get<PopularProduct[]>(`/stats/popular?limit=${limit}`, false);
    } catch (error) {
      console.warn('Failed to fetch popular products:', error);
      return [];
    }
  },

  // Get active merchants (public, no auth)
  async getMerchants(params?: {
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: Merchant[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const searchParams = new URLSearchParams();

    if (params?.category) searchParams.append('merchant_type', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const qs = searchParams.toString();
    const data = await coreAPI.get<{ items?: Merchant[]; pagination?: any }>(`/merchants/active${qs ? `?${qs}` : ''}`, false);

    // API may return array (unwrapped) or object with pagination
    if (Array.isArray(data)) {
      return {
        items: data,
        pagination: { total: data.length, page: params?.page || 1, limit: params?.limit || 12, totalPages: 1 },
      };
    }

    return {
      items: (data as any) || [],
      pagination: (data as any)?.pagination || { total: 0, page: 1, limit: 12, totalPages: 0 },
    };
  },

  // Get merchants by GUIDs (batch lookup)
  async getMerchantsByGuids(guids: string[]): Promise<MerchantPublicInfo[]> {
    if (guids.length === 0) return [];

    try {
      return await coreAPI.post<MerchantPublicInfo[]>('/merchants/batch', {
        merchant_guids: guids,
      }, false); // Public endpoint
    } catch (error) {
      console.warn('Failed to fetch merchants by GUIDs:', error);
      return [];
    }
  },

  // Profile endpoints
  async getMyProfile(): Promise<User> {
    return coreAPI.get<User>('/users/me');
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    return coreAPI.put<User>('/users/me', data);
  },

  // Address endpoints
  async getMyAddresses(): Promise<UserAddress[]> {
    return coreAPI.get<UserAddress[]>('/users/me/addresses');
  },

  async addAddress(address: CreateAddressRequest): Promise<UserAddress> {
    return coreAPI.post<UserAddress>('/users/me/addresses', address);
  },

  async updateAddress(id: string, address: UpdateAddressRequest): Promise<UserAddress> {
    return coreAPI.put<UserAddress>(`/users/me/addresses/${id}`, address);
  },

  async deleteAddress(id: string): Promise<void> {
    return coreAPI.delete<void>(`/users/me/addresses/${id}`);
  },

  async setDefaultAddress(id: string): Promise<UserAddress> {
    return coreAPI.put<UserAddress>(`/users/me/addresses/${id}/default`, {});
  },

  // Order endpoints
  async getMyOrders(params?: { fulfillmentStatus?: string; paymentStatus?: string; page?: number; limit?: number }): Promise<OrdersResponse> {
    const query = new URLSearchParams();
    if (params?.fulfillmentStatus) query.set('fulfillmentStatus', params.fulfillmentStatus);
    if (params?.paymentStatus) query.set('paymentStatus', params.paymentStatus);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return coreAPI.get<OrdersResponse>(`/users/me/orders${qs ? `?${qs}` : ''}`);
  },

  async getOrderTimeline(orderGuid: string): Promise<OrderTimelineEvent[]> {
    return coreAPI.get<OrderTimelineEvent[]>(`/users/me/orders/${orderGuid}/timeline`);
  },

  // Merchant creation
  async createMerchant(data: {
    businessName: string;
    merchantType: string;
    subdomain?: string;
    description?: string;
    countryCode?: string;
    timezone?: string;
    primaryLanguage?: string;
    contactInfo?: { email?: string; phone?: string; website?: string };
    paymentMethods?: string[];
  }): Promise<MerchantCreateResult> {
    // Backend (MREG-1.2) returns either the full merchant (slot available) or a
    // slim waitlist entry { status:'waitlisted', merchantGuid, businessName,
    // subdomain, position } when no slot is free.
    return coreAPI.post<MerchantCreateResult>('/merchants', data);
  },

  // Update merchant
  async updateMerchant(merchantGuid: string, data: {
    businessName?: string;
    description?: string;
    currency?: 'ARS' | 'USD' | 'BRL' | 'EUR';
    contactInfo?: { email?: string; phone?: string; website?: string };
    address?: { street: string; city: string; country: string; state?: string; postalCode?: string } | null;
    paymentMethods?: string[];
    timezone?: string;
    primaryLanguage?: string;
  }): Promise<Merchant> {
    return coreAPI.put<Merchant>(`/merchants/${merchantGuid}`, data);
  },

  // Upload merchant logo
  async uploadMerchantLogo(merchantGuid: string, file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return coreAPI.postFormData<{ logoUrl: string }>(`/merchants/${merchantGuid}/logo`, formData);
  },

  // Provisioning status (MPROV-7)
  async getProvisioningStatus(merchantGuid: string): Promise<{
    provisioningStatus: string;
    provisioningError: string | null;
    merchantGuid: string;
    subdomain: string;
  }> {
    return coreAPI.get(`/merchants/${merchantGuid}/provisioning-status`);
  },

  // Check subdomain availability
  async checkSubdomainAvailability(subdomain: string): Promise<{ available: boolean }> {
    return coreAPI.get<{ available: boolean }>(
      `/merchants/check-subdomain?subdomain=${encodeURIComponent(subdomain)}`,
      false
    );
  },

  // Avatar endpoints (Story 9.8.4)
  async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return coreAPI.postFormData<{ avatar_url: string }>('/users/me/avatar', formData);
  },

  async setAvatarFromUrl(sourceUrl: string): Promise<{ avatar_url: string }> {
    return coreAPI.post<{ avatar_url: string }>('/users/me/avatar', { source_url: sourceUrl });
  },

  async removeAvatar(): Promise<void> {
    return coreAPI.delete<void>('/users/me/avatar');
  },
};